import { DownOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/core';
import Chip from '@mui/material/Chip';
import { Select, Tag } from 'antd';
import { ActionStatus, Comment } from 'mdello-client-ts';
import { Content } from 'mdello-client-ts/dist/src/types/mdello/cms.type';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import CButton from 'src/component/Button';
import Copy from 'src/component/Copy';
import CustomDatePicker from 'src/component/DatePicker';
import InnerHeader from 'src/component/InnerHeader';
import Modal from 'src/component/Modal/Modal';
import { AutocompleteSelect } from 'src/component/Select';
import NoxTable from 'src/component/Table';
import { SearchBy } from 'src/component/Table/enums/searchby.enum';
import formatDate from 'src/helpers/formatDate';
import { useGetContentsQuery } from 'src/reactQuery/Content/useGetContents';
import { useGetCustomersPaginatedQuery } from 'src/reactQuery/customers/queries/useGetCustomersPaginated';
import { useGetZohoDeals } from 'src/reactQuery/payments/zohoDeals/queries/useGetZohoDeals';
import { useAddComment } from 'src/reactQuery/requests/citizenRequest/useAddComment';
import { useGetCitizenRequests } from 'src/reactQuery/requests/citizenRequest/useGetCitizenRequests';
import { useCreateRequestAction } from 'src/reactQuery/requests/createRequestAction';
import { CreateRequestInterface } from 'src/reactQuery/requests/interfaces/createRequest.interface';
import { CitizenRequestsInterface } from 'src/reactQuery/requests/interfaces/requestsResponse.interface';
import { useDeleteTrainings } from 'src/reactQuery/requests/trainings/useDeleteRequests';
import { useUpdateTrainings } from 'src/reactQuery/requests/trainings/useUpdateRequests';
import { Customer } from 'src/services/napi/customers/interfaces/fetchCustomers.interface';
import { ZohoDeal } from 'src/services/napi/zohoDeals/interfaces/zohoDeal.interface';
import { isCurrentProjectCitizen, isCurrentProjectMSquare } from 'src/util/projectCheckers';
import { CStandardInput } from '../../component/Input';

const { Option } = Select;

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec'
];

const useStyles = makeStyles({
  comment: {
    margin: 0,
    padding: '10px 20px 10px 0px'
  },
  comments: {
    borderLeft: '3px solid lightgrey',
    maxHeight: '500px',
    overflow: 'auto',
    paddingLeft: '20px'
  },
  date: {
    color: '#808080',
    margin: 0,
    padding: 0
  },
  input: {
    border: '1px solid gray',
    borderRadius: '10px',
    height: '45px',
    outline: 0,
    padding: '10px',
    width: '100%'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  select: {
    marginTop: '16px'
  },
  text: {
    margin: 0,
    marginBottom: '5px',
    padding: 0
  },
  username: {
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
    marginBottom: '8px',
    padding: 0
  }
});

interface RequestTableProps {
  _id: string;
  comments: Comment[];
  createdAt: Date;
  key: number;
  status: ActionStatus;
  updatedAt: Date;
  username: string;
}

interface TableActionsProps {
  onClick: () => void;
  title: string;
  type?: string;
}

const Requests = () => {
  const classes = useStyles();
  const commentsContainerRef = useRef<HTMLDivElement | null>(null);
  const [requests, setRequests] = useState<RequestTableProps[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[] | undefined>([]);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [request, setRequest] = useState<CitizenRequestsInterface | null>(null);
  const [userComment, setUserComment] = useState('');
  const [requestData, setRequestData] = useState<CreateRequestInterface | null>(null);
  const [selectedUser, setSelectedUser] = useState<Customer | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<ZohoDeal[] | []>([]);
  const [selectedProject, setSelectedProject] = useState<Content<any> | null>(null);

  const { data: CitizenRequestsResponseData, refetch } = useGetCitizenRequests();
  const { data: projectsData } = useGetContentsQuery({
    customTypeId: '652c30627998bb28b7600c4b',
    pageSize: '10000'
  });

  const { data: customerData } = useGetCustomersPaginatedQuery({
    customerId: '',
    page: '1',
    pageSize: '10000',
    status: 'all'
  });
  const { data: zohoDealsData } = useGetZohoDeals({
    page: '1',
    pageSize: '1000000000000'
  });

  const { mutate: deleteMutation } = useDeleteTrainings({
    onSuccess: () => {
      refetch();
    }
  });
  const { mutate: updateMutation } = useUpdateTrainings({
    onSuccess: () => {
      refetch();
    }
  });
  const { mutate: addCommentMutation } = useAddComment({
    onSuccess: () => {
      refetch();
    }
  });
  const { mutate: createRequestActions } = useCreateRequestAction({
    onSuccess: () => {
      refetch();
    }
  });

  const mSquareProjects = useMemo(() => {
    if (!projectsData || !isCurrentProjectMSquare) return [];

    return projectsData.content;
  }, [projectsData]);

  useEffect(() => {
    if (!CitizenRequestsResponseData) {
      return;
    }

    // mapToTable
    const filteredRequests: RequestTableProps[] = CitizenRequestsResponseData.data.map(
      (request, index) => {
        const { alternativeDate, preferredDate, type } = request.data;
        const apartmentNumber = request.metadata?.apartmentNumber;
        const {
          _id,
          category,
          createdAt,
          comments,
          description,
          username,
          updatedAt,
          status
        } = request;

        return {
          _id,
          alternativeDate,
          apartmentNumber,
          category,
          comments,
          createdAt,
          description,
          key: index,
          preferredDate,
          status: status?.toLocaleLowerCase() as ActionStatus,
          type,
          updatedAt,
          username
        };
      }
    );

    setRequests(filteredRequests);
  }, [CitizenRequestsResponseData]);

  const columns = [
    {
      dataIndex: '_id',
      key: 'id',
      render: (_id: any) => {
        return <Copy text={_id} />;
      },
      sorter: (a: any, b: any) => a._id.localeCompare(b._id),
      title: 'Id'
    },
    {
      dataIndex: 'username',
      key: 'username',
      render: (username: any) => {
        return <Copy text={username} />;
      },
      sorter: (a: any, b: any) => a.username.toString().localeCompare(b.username),
      title: 'Username'
    },
    {
      dataIndex: 'apartmentNumber',
      key: 'apartmentNumber',
      render: (apartmentNumber: any) => {
        return <Copy text={apartmentNumber} />;
      },
      sorter: (a: any, b: any) => a.apartmentNumber.toString().localeCompare(b.apartmentNumber),
      title: 'Apartment Number'
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: any) => {
        return <Copy text={formatDate(createdAt)} />;
      },
      sorter: (a: any, b: any) => a.createdAt.toString().localeCompare(b.createdAt),
      title: 'Created at'
    },
    {
      dataIndex: 'type',
      key: 'type',
      render: (type: any) => {
        return <Copy text={type} />;
      },
      sorter: (a: any, b: any) => a.type.toString().localeCompare(b.type),
      title: 'Type'
    },
    {
      dataIndex: 'description',
      key: 'description',
      render: (description: any) => {
        return <Copy text={description} />;
      },
      sorter: (a: any, b: any) => a.description.toString().localeCompare(b.description),
      title: 'Description'
    },
    {
      dataIndex: 'preferredDate',
      key: 'preferredDate',
      render: (preferredDate: any) => {
        return <Copy text={formatDate(preferredDate)} />;
      },
      sorter: (a: any, b: any) => a.preferredDate.toString().localeCompare(b.preferredDate),
      title: 'Preferred Date'
    },
    {
      dataIndex: 'alternativeDate',
      key: 'alternativeDate',
      render: (alternativeDate: any) => {
        return <Copy text={formatDate(alternativeDate)} />;
      },
      sorter: (a: any, b: any) => a.alternativeDate.toString().localeCompare(b.alternativeDate),
      title: 'Alternative Date'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status: any) => {
        let style = {};
        if (status === 'pending') {
          style = {
            backgroundColor: 'rgba(155, 160, 173, 0.15)',
            color: '#9BA0AD'
          };
        }
        if (status === 'confirmed') {
          style = {
            backgroundColor: 'rgba(65, 204, 156, 0.15)',
            color: '#41CC9C'
          };
        }
        if (status === 'rejected') {
          style = {
            backgroundColor: 'rgba(255, 71, 108, 0.15)',
            color: '#FF476C'
          };
        }
        return (
          <Tag
            key={status}
            style={{
              ...style,
              border: 'none',
              cursor: 'default',
              width: '90%'
            }}
          >
            {status}
          </Tag>
        );
      },
      sorter: (a: any, b: any) => a?.status.toString().localeCompare(b?.status),
      title: 'Status'
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (updatedAt: any) => {
        return <Copy text={formatDate(updatedAt)} />;
      },
      sorter: (a: any, b: any) => a.updatedAt.toString().localeCompare(b.updatedAt),
      title: 'Updated at'
    }
  ];

  const onRequestDelete = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = CitizenRequestsResponseData?.data[idx];

      if (item) {
        deleteMutation(item._id);
      }
    });

    setSelectedRowKeys([]);
  };

  const onApprove = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = CitizenRequestsResponseData?.data[idx];

      if (item) {
        updateMutation({
          requestId: item._id,
          status: ActionStatus.CONFIRMED
        });
      }
    });

    setSelectedRowKeys([]);
  };

  const onReject = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = CitizenRequestsResponseData?.data[idx];

      if (item) {
        updateMutation({
          requestId: item._id,
          status: ActionStatus.REJECTED
        });
      }
    });

    setSelectedRowKeys([]);
  };

  const onAddComment = () => {
    if (!request || !userComment) return;

    addCommentMutation({
      id: request._id,
      text: userComment
    });
    setUserComment('');
  };

  const onUserSelect = (e: Customer) => {
    // @ts-ignore
    setRequestData({ ...requestData, user: e._id });
    setSelectedUser(e);
  };

  const onDealSelect = (e: ZohoDeal[]) => {
    const values = [selectedDeal, e].flat();
    setSelectedDeal(values);
  };

  const removeSelectedDeal = (id: string) => {
    const values = selectedDeal.filter((deal) => deal._id !== id);
    setSelectedDeal(values);
  };

  const onProjectSelect = (e: Content<any>) => {
    // @ts-ignore
    setRequestData({ ...requestData, projectId: e._id });
    setSelectedProject(e);
  };

  const onCreateRequestActionCitizen = () => {
    const { description, status, type } = requestData!;

    selectedDeal.forEach((deal) => {
      const getType = () => {
        switch (type) {
          case 'Site Visit':
            return 'ACTION_DATA_VISIT';
          case 'Change File':
            return 'ACTION_DATA_CHANGE_FILE';
          default:
            return '';
        }
      };

      const selectedUser = customerData?.users?.find((user) => user._id === deal.userId);

      createRequestActions({
        body: {
          category: 'Review document change',
          comments: [],
          createdAt: new Date(),
          data: {
            type: getType()
          },
          description,
          metadata: {
            apartmentNumber: deal?.apartment || '',
            categoryKa: 'გადახედე შეცვლილ დოკუმენტს',
            project: 'CityZen'
          },
          needsUserReview: true,
          project: '62861dcc4bcf408cfc5dc3b9',
          respondedBy: null,
          reviewedAt: new Date(),
          status,
          updatedAt: new Date(),
          user: selectedUser?._id || '',
          username: null
        }
      });
    });
    setRequestData(null);
    setIsCreateModalOpen(false);
    setSelectedProject(null);
    setSelectedUser(null);
  };

  const onCreateRequestAction = () => {
    if (!requestData) return;

    if (isCurrentProjectCitizen) {
      onCreateRequestActionCitizen();

      return;
    }

    const { apartmentId, duration, projectId, user, ...rest } = requestData;

    // calculate timeDurationInfo, even if its float
    const preferredDate = requestData.preferredDate || new Date();
    const localDate = new Date(
      preferredDate.toLocaleString('en-US', {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      })
    );

    let tempDuration = 1;
    let tempHours: string | number = preferredDate.getHours();
    let tempMinutes: string | number = preferredDate.getMinutes();
    let dateHours: string | number = preferredDate.getHours();
    let dateMinutes: string | number = preferredDate.getMinutes();

    if (duration) {
      tempDuration = Number(duration);
    }

    // calculate hours and minutes for float duration
    if (!Number.isInteger(tempDuration)) {
      const tempDurationString = String(tempDuration);
      const tempDurationArray = tempDurationString.split('.');
      const floatNumber = Number(tempDurationArray[1]) / 10 ** tempDurationArray[1].length;

      // if sum is more then 24 hours
      if (Number(tempDurationArray[0]) + tempHours > 24) {
        tempHours = Number(tempDurationArray[0]) + tempHours - 24;
      } else {
        tempHours += Number(tempDurationArray[0]);
      }

      // if sum is more then 60 minutes
      if (Math.ceil(60 * floatNumber) + tempMinutes > 60) {
        tempMinutes = Math.ceil(60 * floatNumber) + tempMinutes - 60;
        tempHours += 1;
      } else {
        tempMinutes += Math.ceil(60 * floatNumber);
      }
    } else if (tempDuration + tempHours > 24) {
      // if sum is more then 24 hours

      tempHours = tempDuration + tempHours - 24;
    } else {
      tempHours += tempDuration;
    }

    // check if any of dates is 0
    if (tempHours === 0) {
      tempHours = `${String(tempHours)}0`;
    }
    if (tempMinutes === 0) {
      tempMinutes = `${String(tempMinutes)}0`;
    }
    if (dateHours === 0) {
      dateHours = `${String(dateHours)}0`;
    }
    if (dateMinutes === 0) {
      dateMinutes = `${String(dateMinutes)}0`;
    }

    const timeDurationInfo = `${dateHours}:${dateMinutes}-${tempHours}:${tempMinutes}`;

    const status = ActionStatus[rest?.status?.toUpperCase() as keyof typeof ActionStatus];

    if (!user) {
      if (
        // eslint-disable-next-line no-alert
        window.confirm(
          'Warning - This will create invites for all users in this apartment. Continue?'
        )
      ) {
        customerData?.users?.forEach((user) => {
          createRequestActions({
            body: {
              user: user._id,
              ...rest,
              data: {
                apartmentId,
                duration,
                needsUserReview: status === ActionStatus.CONFIRMED.toLowerCase(),
                preferredDate: localDate,
                projectId,
                timeDurationInfo,
                type: 'ACTION_DATA_VISIT'
              },
              metadata: {
                categoryKa: 'ბინის ვიზიტი'
              },
              status
            }
          });
        });

        setRequestData(null);
        setIsCreateModalOpen(false);
        setSelectedProject(null);
        setSelectedUser(null);
      } else {
        setRequestData(null);
        setIsCreateModalOpen(false);
        setSelectedProject(null);
        setSelectedUser(null);
      }
    } else {
      createRequestActions({
        body: {
          user,
          ...rest,
          data: {
            apartmentId,
            duration,
            needsUserReview: status === ActionStatus.CONFIRMED.toLowerCase(),
            preferredDate: localDate,
            projectId,
            timeDurationInfo,
            type: 'ACTION_DATA_VISIT'
          },
          metadata: {
            categoryKa: 'ბინის დაჯავშნა'
          },
          status
        }
      });
      setRequestData(null);
      setIsCreateModalOpen(false);
      setSelectedProject(null);
      setSelectedUser(null);
    }
  };

  function formatterForCommentsDate(date: Date) {
    if (!date) return;

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  }

  function scrollToBottomCommentsContainer() {
    if (commentsContainerRef.current) {
      commentsContainerRef.current.scrollTo({
        behavior: 'smooth',
        top: commentsContainerRef.current.scrollHeight
      });
    }
  }

  useEffect(() => {
    if (CitizenRequestsResponseData?.data && selectedRowKeys?.length === 1) {
      const selectedIndex = selectedRowKeys[0];
      const selectedItem = CitizenRequestsResponseData.data[selectedIndex];

      setRequest(selectedItem);
    } else if (request !== null) {
      setRequest(null);
    }
  }, [isAddCommentModalOpen, CitizenRequestsResponseData, selectedRowKeys]);

  useEffect(() => {
    if (!request || !isAddCommentModalOpen) return;

    scrollToBottomCommentsContainer();
  }, [request]);

  const actions: TableActionsProps[] = [
    {
      onClick: () => setIsCreateModalOpen(true),
      title: 'Add Request Action',
      type: 'primary'
    }
  ];

  if (selectedRowKeys?.length) {
    if (selectedRowKeys?.length === 1) {
      actions.unshift({
        onClick: () => setIsAddCommentModalOpen(true),
        title: 'Add Comment',
        type: 'secondaryModified'
      });
    }

    actions.unshift(
      {
        onClick: onRequestDelete,
        title: 'Delete'
      },
      {
        onClick: onApprove,
        title: 'Approve',
        type: 'secondaryModified'
      },
      {
        onClick: onReject,
        title: 'Reject',
        type: 'secondaryModified'
      }
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={CitizenRequestsResponseData?.paging?.total} title="Requests" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={requests}
        searchBy={(SearchBy.TITLE, SearchBy.APARTMENT_NUMBER)}
        // @ts-ignore
        selectedRowKeys={selectedRowKeys}
        // @ts-ignore
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <Modal
        footerActions={
          <>
            <input
              className={classes.input}
              onChange={(e) => setUserComment(e.target.value)}
              placeholder="Add Comment"
              type="text"
              value={userComment}
            />

            <CButton onClick={onAddComment} type="primary">
              Add Comment
            </CButton>
          </>
        }
        handleClose={() => setIsAddCommentModalOpen(false)}
        isModalOpen={isAddCommentModalOpen}
        styles={{
          // @ts-ignore
          '& .MuiDialogActions-root': {
            alignItems: 'center',
            justifyContent: 'space-between'
          },
          '& .MuiDialogContent-root': {
            paddingBottom: '24px',
            paddingTop: '5px'
          },
          width: '560px'
        }}
        titleContent={<h4 className="modal-title">Comments</h4>}
      >
        <div ref={commentsContainerRef} className={classes.comments}>
          {request?.comments?.map((item) => {
            const date = formatterForCommentsDate(item?.createdAt);

            return (
              <div key={item.id} className={classes.comment}>
                <p className={classes.username}>{item?.username}</p>

                <p className={classes.text}>{item?.text}</p>

                <p className={classes.date}>{date}</p>
              </div>
            );
          })}
        </div>
      </Modal>

      <Modal
        footerActions={
          <>
            <CButton onClick={onCreateRequestAction} type="primary">
              Create
            </CButton>
          </>
        }
        handleClose={() => {
          setIsCreateModalOpen(false);
          setSelectedUser(null);
          setSelectedProject(null);
        }}
        isModalOpen={isCreateModalOpen}
        styles={{
          // @ts-ignore
          '& .MuiDialogActions-root': {
            alignItems: 'center',
            justifyContent: 'flex-end'
          },
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            paddingBottom: '24px',
            paddingTop: '12px'
          },
          width: '400px'
        }}
        titleContent={<h4 className="modal-title">Create Request Actions</h4>}
      >
        <Select
          className={classes.select}
          dropdownRender={(menu) => <div>{menu}</div>}
          dropdownStyle={{
            border: '1px solid #CAD8FF',
            borderRadius: '0px 0px 14px 14px',
            fontFamily: 'Poppins',
            padding: '0px',
            transform: 'translateY(5px) !important',
            width: '32px',
            zIndex: '2000'
          }}
          onChange={(type) => {
            const getType = () => {
              switch (type) {
                case 'ACTION_DATA_VISIT':
                  return 'Site Visit';
                case 'ACTION_DATA_CHANGE_FILE':
                  return 'Change File';
                default:
                  return '';
              }
            };
            // @ts-ignore
            setRequestData({ ...requestData, type: getType() });
          }}
          suffixIcon={
            // @ts-ignore
            <DownOutlined
              style={{
                color: 'black',
                position: 'absolute',
                right: '8px'
              }}
            />
          }
          value={requestData?.type || ''}
        >
          <Option key="ACTION_DATA_VISIT">Site Visit</Option>
          <Option key="ACTION_DATA_CHANGE_FILE">Change File</Option>
        </Select>
        <Select
          className={classes.select}
          dropdownRender={(menu) => <div>{menu}</div>}
          dropdownStyle={{
            border: '1px solid #CAD8FF',
            borderRadius: '0px 0px 14px 14px',
            fontFamily: 'Poppins',
            padding: '0px',
            transform: 'translateY(5px) !important',
            width: '32px',
            zIndex: '2000'
          }}
          onChange={(e: ActionStatus) => {
            // @ts-ignore
            setRequestData({ ...requestData, status: e });
          }}
          suffixIcon={
            // @ts-ignore
            <DownOutlined
              style={{
                color: 'black',
                position: 'absolute',
                right: '8px'
              }}
            />
          }
          // @ts-ignore
          value={requestData?.status || ''}
        >
          {Object.values(ActionStatus)
            .filter((status) => {
              return status !== ActionStatus.USER_ACCEPTED && status !== ActionStatus.USER_REJECTED;
            })
            .map((status) => {
              return <Option key={status}>{status}</Option>;
            })}
        </Select>

        {isCurrentProjectMSquare && (
          <AutocompleteSelect
            getOptionLabel={(option) => option.name || '-'}
            id="autocomplete-ProjectBlock"
            onSelect={onProjectSelect}
            onViewAll={false}
            options={mSquareProjects || []}
            placeholderValue={selectedProject?.name || 'Select Project'}
            styles={{
              width: '100%'
            }}
          />
        )}

        {isCurrentProjectMSquare && (
          <AutocompleteSelect
            getOptionLabel={(option) => `${option.fullName || ''} - ${option.idNumber || ''}`}
            id="autocomplete-ProjectBlock"
            onSelect={onUserSelect}
            onViewAll={false}
            options={customerData?.users || []}
            placeholderValue={
              `${selectedUser?.fullName || ''} - ${selectedUser?.idNumber || ''}` || 'Select User'
            }
            styles={{
              width: '100%'
            }}
          />
        )}

        {isCurrentProjectCitizen && (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                textAlign: 'start'
              }}
            >
              {selectedDeal.map((deal) => {
                return (
                  <Chip
                    key={deal._id}
                    label={deal.name}
                    onClick={() => {
                      navigator.clipboard.writeText(deal.name);
                    }}
                    onDelete={() => removeSelectedDeal(deal._id)}
                  />
                );
              })}
            </div>
            <AutocompleteSelect
              getOptionLabel={(option) => option?.name}
              id="autocomplete-ProjectBlock"
              multiple
              onSelect={onDealSelect}
              onViewAll={false}
              options={
                zohoDealsData?.deals.filter((deal) => deal).filter((deal) => deal.userId) || []
              }
              placeholderValue={selectedDeal?.map((deal) => deal.name) || 'Select Deal'}
              styles={{
                width: '100%'
              }}
            />
          </>
        )}

        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-create-action-desc"
          id="standard-input-create-action-desc"
          label="Description"
          multiline
          onChange={(e: FormEvent<HTMLInputElement>) => {
            // @ts-ignore
            setRequestData({ ...requestData, description: e.target.value });
          }}
          rows="3"
          size="large"
          value={requestData?.description || ''}
          width="true"
        />

        {!isCurrentProjectCitizen && (
          <>
            <CStandardInput
              disableUnderline
              formclasses={{ root: classes.root }}
              htmlFor="standard-input-create-action-apartment"
              id="standard-input-create-action-apartment"
              label="Apartment ID"
              onChange={(e: FormEvent<HTMLInputElement>) => {
                // @ts-ignore
                setRequestData({ ...requestData, apartmentId: e.target.value });
              }}
              size="large"
              value={requestData?.apartmentId || ''}
              width="true"
            />

            <CStandardInput
              disableUnderline
              formclasses={{ root: classes.root }}
              htmlFor="standard-input-create-action-duration"
              id="standard-input-create-action-duration"
              label="Duration (in hours)"
              onChange={(e: FormEvent<HTMLInputElement>) => {
                // @ts-ignore
                setRequestData({ ...requestData, duration: e.target.value });
              }}
              size="large"
              value={requestData?.duration || ''}
              width="true"
            />

            <CustomDatePicker
              // @ts-ignore
              onChange={(e: Date) => setRequestData({ ...requestData, preferredDate: e })}
              value={requestData?.preferredDate}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default Requests;
